import { AdminSettings, AircraftType, Airstrip, Leg, Quote, AdvisorySeverity, AgentModule, AgentEnforcementPolicy, AgentCustomRule, QuoteAdvisorySection, RuleCondition } from '../types';

export type { AdvisorySeverity } from '../types';

export interface Advisory {
  id: string;
  severity: AdvisorySeverity;
  title: string;
  detail: string;
  suggestion?: string;
  section?: QuoteAdvisorySection | string;
  legId?: string;
  legIndex?: number;
}

export const ruleCatalog: Record<AgentModule, { id: string; title: string; severity: AdvisorySeverity; description: string }[]> = {
  QUOTER: [
    { id: 'charter.no_legs', title: 'No legs added', severity: 'HIGH', description: 'A charter mission must have at least one leg.' },
    { id: 'charter.customer_missing', title: 'Client not selected', severity: 'MEDIUM', description: 'A charter quote should be attached to a customer.' },
    { id: 'charter.aircraft_missing', title: 'Aircraft not selected', severity: 'MEDIUM', description: 'Select an aircraft model before authorizing.' },
    { id: 'charter.leg.missing_route', title: 'Leg missing route', severity: 'HIGH', description: 'Each leg requires origin and destination.' },
    { id: 'charter.past_date', title: 'Date is in the past', severity: 'MEDIUM', description: 'Prevent past-dated missions.' },
    { id: 'charter.discount_cap', title: 'Discount exceeds cap', severity: 'MEDIUM', description: 'Enforce role-based discount caps.' },
    { id: 'charter.leg.same_route', title: 'Same origin/destination', severity: 'MEDIUM', description: 'Leg must have different origin/destination.' },
    { id: 'charter.leg.pax_zero', title: 'PAX is zero', severity: 'HIGH', description: 'Passenger count must be > 0 unless dead leg.' },
    { id: 'charter.leg.block_time', title: 'Block time missing', severity: 'MEDIUM', description: 'Block time minutes must be > 0.' },
    { id: 'charter.leg.pax_over', title: 'Exceeds aircraft capacity', severity: 'HIGH', description: 'PAX must not exceed aircraft max.' },
    { id: 'charter.leg.unknown_airstrip', title: 'Unknown airstrip', severity: 'LOW', description: 'Destination must exist in registry.' },
    { id: 'charter.leg.immigration_required', title: 'Immigration stop required', severity: 'LOW', description: 'International charter route requires border-post handling.' },
    { id: 'charter.return_base', title: 'Return-to-base missing', severity: 'LOW', description: 'Last leg should return to base.' },
  ],
  SCHEDULED: [
    { id: 'scheduled.past_date', title: 'Date is in the past', severity: 'MEDIUM', description: 'Prevent past-dated bookings.' },
    { id: 'scheduled.route_invalid', title: 'Invalid route', severity: 'HIGH', description: 'Origin and destination must be different.' },
    { id: 'scheduled.pax_zero', title: 'PAX is zero', severity: 'HIGH', description: 'Passenger count must be > 0.' },
    { id: 'scheduled.unknown_airstrip', title: 'Unknown destination airstrip', severity: 'LOW', description: 'Destination must exist in registry.' },
    { id: 'scheduled.ma_fee', title: 'Meet & Assist fee missing', severity: 'LOW', description: 'Fee missing for selected service.' },
    { id: 'scheduled.lounge_fee', title: 'Lounge fee missing', severity: 'LOW', description: 'Fee missing for selected service.' },
  ],
  PLANNER: [
    { id: 'planner.unassigned_tail', title: 'Unassigned aircraft', severity: 'MEDIUM', description: 'Leg requires an aircraft before dispatch.' },
    { id: 'planner.crew_missing', title: 'Crew not assigned', severity: 'MEDIUM', description: 'Leg requires crew assignment.' },
    { id: 'planner.time_missing', title: 'ETD/ETA missing', severity: 'LOW', description: 'Timing required for coordination.' },
  ],
  FUEL: [
    { id: 'fuel.aircraft_missing', title: 'Aircraft missing', severity: 'HIGH', description: 'Select a tail number for uplift.' },
    { id: 'fuel.liters_zero', title: 'Invalid uplift quantity', severity: 'HIGH', description: 'Uplift liters must be > 0.' },
    { id: 'fuel.airstrip_missing', title: 'Unknown station', severity: 'MEDIUM', description: 'Fuel location must exist.' },
    { id: 'fuel.not_authorized', title: 'Fuel not authorized', severity: 'HIGH', description: 'Station marked as no-fuel.' },
    { id: 'fuel.overfill', title: 'Exceeds remaining capacity', severity: 'MEDIUM', description: 'Uplift exceeds capacity.' },
  ],
  WHATSAPP: [
    { id: 'whatsapp.tone.casual_phrase', title: 'Casual phrase detected', severity: 'MEDIUM', description: 'Replace casual language with premium client tone.' },
    { id: 'whatsapp.promise.vague', title: 'Vague promise made', severity: 'HIGH', description: 'Avoid uncertain promises without a clear next step.' },
    { id: 'whatsapp.reply.missing_next_step', title: 'Reply missing next step', severity: 'MEDIUM', description: 'Responses should guide the client on what happens next.' },
    { id: 'whatsapp.lookup.reference_detected', title: 'Reference lookup available', severity: 'LOW', description: 'Estimate, quote, or customer reference can be pulled into context.' },
    { id: 'whatsapp.followup.pending', title: 'Follow-up promise pending', severity: 'HIGH', description: 'Operator promised a follow-up and the thread still needs action.' },
  ],
  EMAIL: [
    { id: 'email.reply.missing_signature', title: 'Signature missing', severity: 'LOW', description: 'Outbound email should include a professional signature.' },
    { id: 'email.reply.missing_next_step', title: 'No clear next step', severity: 'MEDIUM', description: 'Reply should clarify the next action or expected timeline.' },
    { id: 'email.thread.long_unanswered', title: 'Thread unanswered too long', severity: 'HIGH', description: 'Inbound thread is open without a response for too long.' },
  ],
};

export const ruleTemplates: Record<AgentModule, AgentCustomRule[]> = {
  QUOTER: [
    {
      id: 'template.charter.discount_high',
      title: 'Discount over 15%',
      severity: 'MEDIUM',
      detail: 'Discount exceeds recommended 15% threshold.',
      suggestion: 'Re-check discount or request approval.',
      section: 'DISCOUNT',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'discount', op: 'gt', value: 15 }],
    },
    {
      id: 'template.charter.no_legs',
      title: 'No legs created',
      severity: 'HIGH',
      detail: 'A charter mission must include at least one leg.',
      suggestion: 'Add the mission legs before sending.',
      section: 'ROUTE',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'totals.legsCount', op: 'eq', value: 0 }],
    },
  ],
  SCHEDULED: [
    {
      id: 'template.scheduled.pax_over_12',
      title: 'PAX exceeds 12',
      severity: 'HIGH',
      detail: 'Scheduled booking exceeds seat capacity.',
      suggestion: 'Split booking or assign larger aircraft.',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'paxCount', op: 'gt', value: 12 }],
    },
  ],
  PLANNER: [
    {
      id: 'template.planner.unassigned_tail',
      title: 'Any unassigned tails',
      severity: 'MEDIUM',
      detail: 'One or more legs are unassigned.',
      suggestion: 'Assign aircraft before dispatch.',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'quotes.length', op: 'gt', value: 0 }],
    },
  ],
  FUEL: [
    {
      id: 'template.fuel.uplift_high',
      title: 'Uplift over 1000L',
      severity: 'HIGH',
      detail: 'Uplift exceeds safety threshold.',
      suggestion: 'Confirm with DFO or split uplift.',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'liters', op: 'gt', value: 1000 }],
    },
  ],
  WHATSAPP: [
    {
      id: 'template.whatsapp.casual_phrase',
      title: 'Casual tone phrase',
      severity: 'MEDIUM',
      detail: 'Draft includes casual wording that is below premium-service tone.',
      suggestion: 'Use a professional phrase and explain the next action clearly.',
      enabled: true,
      logic: 'any',
      conditions: [
        { field: 'draftText', op: 'contains', value: 'let me check' },
        { field: 'draftText', op: 'contains', value: 'i dont know' },
        { field: 'draftText', op: 'contains', value: 'not sure' },
      ],
    },
    {
      id: 'template.whatsapp.missing_next_step',
      title: 'No clear next step',
      severity: 'MEDIUM',
      detail: 'Draft replies should tell the client what will happen next.',
      suggestion: 'Add a concrete next step or expected follow-up.',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'hasNextStep', op: 'eq', value: false }],
    },
  ],
  EMAIL: [
    {
      id: 'template.email.missing_signature',
      title: 'Missing signature block',
      severity: 'LOW',
      detail: 'Outbound draft appears to be missing a signature.',
      suggestion: 'Attach user signature before sending.',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'hasSignature', op: 'eq', value: false }],
    },
    {
      id: 'template.email.no_next_step',
      title: 'No next-step commitment',
      severity: 'MEDIUM',
      detail: 'Reply does not communicate what happens next.',
      suggestion: 'Add an ETA or clear next action.',
      enabled: true,
      logic: 'all',
      conditions: [{ field: 'hasNextStep', op: 'eq', value: false }],
    },
  ],
};

const todayIso = () => new Date().toISOString().split('T')[0];
const canonicalizeBaseCode = (value?: string | null) => {
  const code = String(value || '').trim().toUpperCase();
  if (!code) return '';
  if (code === 'ARK' || code === 'HTAR' || code === 'ARUSHA') return 'HTAR';
  return code;
};

const isImmigrationGeneratedLeg = (leg?: Partial<Leg> | null) => {
  const id = String(leg?.id || '').toUpperCase();
  return id.startsWith('AUTO-IMM-') || id.includes('-AUTO-IMM-');
};

const normalizeQuoteSection = (value?: string | null): QuoteAdvisorySection | undefined => {
  const key = String(value || '').trim().toUpperCase();
  if (
    key === 'MISSION' ||
    key === 'ROUTE' ||
    key === 'TIMING' ||
    key === 'SERVICES' ||
    key === 'PRICING' ||
    key === 'DISCOUNT' ||
    key === 'FINAL'
  ) {
    return key as QuoteAdvisorySection;
  }
  return undefined;
};

export const filterAdvisories = (advisories: Advisory[], settings: AdminSettings, module: AgentModule) => {
  const toggles = settings.agent?.ruleToggles?.[module] || {};
  return advisories.filter(a => (toggles[a.id] !== undefined ? toggles[a.id] : true));
};

export const getEnforcementPolicy = (settings: AdminSettings, module: AgentModule): AgentEnforcementPolicy => {
  return settings.agent?.enforcementByModule?.[module] || settings.agent?.enforcement || {
    blockSeverities: ['HIGH'],
    allowOverride: true,
    requireOverrideReason: true,
  };
};

const resolvePath = (obj: any, path: string) => {
  const normalized = path.replace(/\[(\d+)\]/g, '.$1');
  return normalized.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
};

const evalCondition = (condition: RuleCondition, context: any) => {
  const left = resolvePath(context, condition.field);
  const right = condition.value;
  switch (condition.op) {
    case 'eq': return left === right;
    case 'neq': return left !== right;
    case 'gt': return typeof left === 'number' && typeof right === 'number' && left > right;
    case 'gte': return typeof left === 'number' && typeof right === 'number' && left >= right;
    case 'lt': return typeof left === 'number' && typeof right === 'number' && left < right;
    case 'lte': return typeof left === 'number' && typeof right === 'number' && left <= right;
    case 'in': return Array.isArray(right) && (right as any[]).includes(left);
    case 'notIn': return Array.isArray(right) && !(right as any[]).includes(left);
    case 'contains':
      if (typeof left === 'string' && typeof right === 'string') return left.includes(right);
      if (Array.isArray(left)) return Array.isArray(right) ? right.every(v => left.includes(v)) : left.includes(right);
      return false;
    case 'notEmpty': return left !== undefined && left !== null && left !== '' && !(Array.isArray(left) && left.length === 0);
    case 'empty': return left === undefined || left === null || left === '' || (Array.isArray(left) && left.length === 0);
    default: return false;
  }
};

export const evaluateCustomRules = (rules: AgentCustomRule[] | undefined, context: any): Advisory[] => {
  if (!rules || !Array.isArray(rules)) return [];
  return rules
    .filter(r => r.enabled !== false)
    .filter(r => Array.isArray(r.conditions) && r.conditions.length > 0)
    .filter(r => {
      const logic = r.logic || 'all';
      const results = r.conditions.map(c => evalCondition(c, context));
      return logic === 'any' ? results.some(Boolean) : results.every(Boolean);
    })
    .map(r => ({
      id: r.id,
      severity: r.severity,
      title: r.title,
      detail: r.detail,
      suggestion: r.suggestion,
      section: normalizeQuoteSection(r.section),
    }));
};

export const validateCustomRules = (rules: AgentCustomRule[] | undefined): string[] => {
  if (!rules) return [];
  const errors: string[] = [];
  rules.forEach((r, idx) => {
    if (!r.id) errors.push(`Rule ${idx + 1}: id is required`);
    if (!r.title) errors.push(`Rule ${idx + 1}: title is required`);
    if (!r.detail) errors.push(`Rule ${idx + 1}: detail is required`);
    if (!r.conditions || r.conditions.length === 0) errors.push(`Rule ${idx + 1}: conditions required`);
    r.conditions?.forEach((c, cidx) => {
      if (!c.field) errors.push(`Rule ${idx + 1} Condition ${cidx + 1}: field is required`);
      if (!c.op) errors.push(`Rule ${idx + 1} Condition ${cidx + 1}: operator is required`);
    });
  });
  return errors;
};

export const getCharterAdvisories = (
  formData: { customerId: string; aircraftModel: string; date: string; discount: number; legs: Leg[] },
  aircrafts: AircraftType[],
  airstrips: Airstrip[],
  settings: AdminSettings,
  currentRole: string
): Advisory[] => {
  const advisories: Advisory[] = [];
  const legs = Array.isArray(formData.legs) ? formData.legs : [];
  const aircraft = aircrafts.find(a => a.name === formData.aircraftModel);
  const discountCap = settings.discountCapsByRole[currentRole as any] || 0;

  if (!String(formData.customerId || '').trim()) {
    advisories.push({
      id: 'charter.customer_missing',
      severity: 'MEDIUM',
      title: 'Client not selected',
      detail: 'Customer identity is still empty on this charter quote.',
      suggestion: 'Select the client before saving or sending.',
      section: 'MISSION',
    });
  }

  if (!String(formData.aircraftModel || '').trim()) {
    advisories.push({
      id: 'charter.aircraft_missing',
      severity: 'MEDIUM',
      title: 'Aircraft not selected',
      detail: 'Aircraft model is empty, so capacity and costing checks are incomplete.',
      suggestion: 'Select the aircraft model before authorizing.',
      section: 'MISSION',
    });
  }

  if (legs.length === 0) {
    advisories.push({
      id: 'charter.no_legs',
      severity: 'HIGH',
      title: 'No legs added',
      detail: 'A charter mission requires at least one leg.',
      suggestion: 'Add an initial leg with route and timing.',
      section: 'ROUTE',
    });
  }

  if (formData.date < todayIso()) {
    advisories.push({
      id: 'charter.past_date',
      severity: 'MEDIUM',
      title: 'Date is in the past',
      detail: `Issue date ${formData.date} is before today.`,
      suggestion: 'Update the issue date to a valid current date.',
      section: 'MISSION',
    });
  }

  if (formData.discount > discountCap) {
    advisories.push({
      id: 'charter.discount_cap',
      severity: 'MEDIUM',
      title: 'Discount exceeds role cap',
      detail: `Discount ${formData.discount}% is above the ${discountCap}% cap for this role.`,
      suggestion: 'Reduce discount or request approval.',
      section: 'DISCOUNT',
    });
  }

  legs.forEach((leg, idx) => {
    const legId = `L${idx + 1}`;
    const legDateValue = String(leg.etd || leg.eta || '').trim();
    const legDate = legDateValue
      ? (/^\d{4}-\d{2}-\d{2}/.test(legDateValue)
          ? legDateValue.slice(0, 10)
          : (() => {
              const parsed = new Date(legDateValue);
              return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
            })())
      : '';
    if (legDate && legDate < todayIso()) {
      advisories.push({
        id: 'charter.leg.past_date',
        severity: 'MEDIUM',
        title: `Leg ${idx + 1} is in the past`,
        detail: `Leg date ${legDate} is before today.`,
        suggestion: 'Update the mission date or ETD.',
        section: 'TIMING',
        legId: leg.id,
        legIndex: idx,
      });
    }
    if (!leg.from || !leg.to) {
      advisories.push({
        id: 'charter.leg.missing_route',
        severity: 'HIGH',
        title: `Leg ${idx + 1} missing route`,
        detail: 'Both origin and destination are required.',
        suggestion: 'Select valid airstrip codes for from/to.',
        section: 'ROUTE',
        legId: leg.id,
        legIndex: idx,
      });
    }
    if (leg.from && leg.to && leg.from === leg.to) {
      advisories.push({
        id: 'charter.leg.same_route',
        severity: 'MEDIUM',
        title: `Leg ${idx + 1} has same origin/destination`,
        detail: `${leg.from} → ${leg.to} is not a valid sector.`,
        suggestion: 'Change destination or mark as positioning leg.',
        section: 'ROUTE',
        legId: leg.id,
        legIndex: idx,
      });
    }
    const fromAirstrip = leg.from ? airstrips.find(a => a.code === leg.from) : undefined;
    const toAirstrip = leg.to ? airstrips.find(a => a.code === leg.to) : undefined;
    const isInternationalLeg = Boolean(
      fromAirstrip &&
      toAirstrip &&
      fromAirstrip.country &&
      toAirstrip.country &&
      fromAirstrip.country !== toAirstrip.country
    );
    if (isInternationalLeg) {
      advisories.push({
        id: 'charter.leg.immigration_required',
        severity: 'LOW',
        title: `Leg ${idx + 1} requires immigration handling`,
        detail: `${leg.from} → ${leg.to} crosses countries, so the system should route through exit and entry border-post airstrips.`,
        suggestion: 'Review the generated immigration stop(s) and confirm the border-post route is operationally correct.',
        section: 'ROUTE',
        legId: leg.id,
        legIndex: idx,
      });
    }
    if (isImmigrationGeneratedLeg(leg)) {
      advisories.push({
        id: `charter.leg.${idx + 1}.immigration_segment`,
        severity: 'LOW',
        title: `Leg ${idx + 1} is an immigration segment`,
        detail: 'This sector was auto-generated to satisfy international exit or entry processing.',
        suggestion: 'Keep it unless operations intentionally replaces the immigration routing.',
        section: 'ROUTE',
        legId: leg.id,
        legIndex: idx,
      });
    }
    const isPositioningLeg = Boolean(leg.isDeadLeg) || Number(leg.paxCount || 0) <= 0;
    if (!isPositioningLeg && (leg.paxCount || 0) <= 0) {
      advisories.push({
        id: 'charter.leg.pax_zero',
        severity: 'HIGH',
        title: `Leg ${idx + 1} pax is zero`,
        detail: 'Passenger count must be greater than zero.',
        suggestion: 'Update pax count or mark as dead leg.',
        section: 'ROUTE',
        legId: leg.id,
        legIndex: idx,
      });
    }
    if ((leg.blockTimeMinutes || 0) <= 0) {
      advisories.push({
        id: 'charter.leg.block_time',
        severity: 'MEDIUM',
        title: `Leg ${idx + 1} block time is empty`,
        detail: 'Block time minutes should be greater than zero.',
        suggestion: 'Enter an estimated block time.',
        section: 'TIMING',
        legId: leg.id,
        legIndex: idx,
      });
    }
    if (aircraft && (leg.paxCount || 0) > (aircraft.maxPax || 0)) {
      advisories.push({
        id: 'charter.leg.pax_over',
        severity: 'HIGH',
        title: `Leg ${idx + 1} exceeds aircraft capacity`,
        detail: `${leg.paxCount} pax exceeds ${aircraft.maxPax} max.`,
        suggestion: 'Reduce pax, switch aircraft, or split legs.',
        section: 'ROUTE',
        legId: leg.id,
        legIndex: idx,
      });
    }
    if (leg.to && !toAirstrip) {
      advisories.push({
        id: 'charter.leg.unknown_airstrip',
        severity: 'LOW',
        title: `Unknown airstrip ${leg.to}`,
        detail: 'Destination not found in registry.',
        suggestion: 'Confirm airstrip code or add it in System Registry.',
        section: 'ROUTE',
        legId: leg.id,
        legIndex: idx,
      });
    }
  });

  const lastLeg = legs[legs.length - 1];
  if (lastLeg && canonicalizeBaseCode(lastLeg.to) !== canonicalizeBaseCode(settings.returnBaseAirstrip)) {
    advisories.push({
      id: 'charter.return_base',
      severity: 'LOW',
      title: 'Return-to-base missing',
      detail: `Last leg ends at ${lastLeg.to}, not ${settings.returnBaseAirstrip}.`,
      suggestion: 'Add a dead leg to return base if required.',
      section: 'FINAL',
      legId: lastLeg.id,
      legIndex: legs.length - 1,
    });
  }

  return advisories;
};

export const getScheduledAdvisories = (
  formData: { customerId: string; from: string; to: string; date: string; paxCount: number; meetAndAssist: boolean; lounge: boolean },
  airstrips: Airstrip[]
): Advisory[] => {
  const advisories: Advisory[] = [];
  if (formData.date < todayIso()) {
    advisories.push({
      id: 'scheduled.past_date',
      severity: 'MEDIUM',
      title: 'Date is in the past',
      detail: `Service date ${formData.date} is before today.`,
      suggestion: 'Update the booking date.',
    });
  }
  if (!formData.from || !formData.to || formData.from === formData.to) {
    advisories.push({
      id: 'scheduled.route_invalid',
      severity: 'HIGH',
      title: 'Invalid route',
      detail: 'Origin and destination must be different.',
      suggestion: 'Select a valid scheduled route.',
    });
  }
  if ((formData.paxCount || 0) <= 0) {
    advisories.push({
      id: 'scheduled.pax_zero',
      severity: 'HIGH',
      title: 'Passenger count is zero',
      detail: 'At least 1 passenger is required.',
      suggestion: 'Enter the correct pax count.',
    });
  }
  const airstrip = airstrips.find(a => a.code === formData.to);
  if (!airstrip) {
    advisories.push({
      id: 'scheduled.unknown_airstrip',
      severity: 'LOW',
      title: 'Unknown destination airstrip',
      detail: `${formData.to} not found in registry.`,
      suggestion: 'Confirm destination or register it.',
    });
  }
  if (formData.meetAndAssist && airstrip && !airstrip.meetAndAssistFee) {
    advisories.push({
      id: 'scheduled.ma_fee',
      severity: 'LOW',
      title: 'Meet & Assist fee missing',
      detail: 'Meet & Assist selected but no fee on record.',
      suggestion: 'Update airstrip fees or deselect the service.',
    });
  }
  if (formData.lounge && airstrip && !airstrip.loungeFee) {
    advisories.push({
      id: 'scheduled.lounge_fee',
      severity: 'LOW',
      title: 'Lounge fee missing',
      detail: 'Lounge selected but no fee on record.',
      suggestion: 'Update airstrip fees or deselect the service.',
    });
  }
  return advisories;
};

export const getPlannerAdvisories = (
  quotes: Quote[]
): Advisory[] => {
  const advisories: Advisory[] = [];
  const legs = quotes.flatMap(q => q.legs.map(l => ({ quoteId: q.id, tail: q.tailNumber, leg: l, status: q.status })));

  legs.forEach(({ quoteId, tail, leg }) => {
    if (!tail || tail === 'UNASSIGNED') {
      advisories.push({
        id: 'planner.unassigned_tail',
        severity: 'MEDIUM',
        title: 'Unassigned aircraft',
        detail: `Leg ${leg.from} → ${leg.to} has no tail assigned.`,
        suggestion: 'Assign a tail before dispatch.',
      });
    }
    if (!leg.assignedCrew || leg.assignedCrew.length === 0) {
      advisories.push({
        id: 'planner.crew_missing',
        severity: 'MEDIUM',
        title: 'Crew not assigned',
        detail: `Leg ${leg.from} → ${leg.to} has no crew.`,
        suggestion: 'Assign crew to meet duty requirements.',
      });
    }
    if (!leg.etd || !leg.eta) {
      advisories.push({
        id: 'planner.time_missing',
        severity: 'LOW',
        title: 'ETD/ETA missing',
        detail: `Leg ${leg.from} → ${leg.to} missing timing.`,
        suggestion: 'Enter ETD/ETA for dispatch coordination.',
      });
    }
  });

  return advisories;
};

export const getFuelAdvisories = (
  formData: { aircraftId: string; liters: number; location: string },
  aircrafts: AircraftType[],
  airstrips: Airstrip[]
): Advisory[] => {
  const advisories: Advisory[] = [];
  const ac = aircrafts.find(a => a.id === formData.aircraftId);
  const as = airstrips.find(a => a.code === formData.location);

  if (!ac) {
    advisories.push({
      id: 'fuel.aircraft_missing',
      severity: 'HIGH',
      title: 'Aircraft missing',
      detail: 'No aircraft selected for uplift.',
      suggestion: 'Select the correct tail number.',
    });
  }
  if ((formData.liters || 0) <= 0) {
    advisories.push({
      id: 'fuel.liters_zero',
      severity: 'HIGH',
      title: 'Invalid uplift quantity',
      detail: 'Fuel uplift must be greater than zero.',
      suggestion: 'Enter the uplift liters.',
    });
  }
  if (!as) {
    advisories.push({
      id: 'fuel.airstrip_missing',
      severity: 'MEDIUM',
      title: 'Unknown station',
      detail: `${formData.location} not found in registry.`,
      suggestion: 'Select a valid station code.',
    });
  } else if (!as.fuelAvailable) {
    advisories.push({
      id: 'fuel.not_authorized',
      severity: 'HIGH',
      title: 'Fuel not authorized at station',
      detail: `${as.code} is marked as fuel unavailable.`,
      suggestion: 'Select an authorized fuel station.',
    });
  }

  if (ac && ac.maxFuel !== undefined && ac.currentFuel !== undefined) {
    const remaining = ac.maxFuel - ac.currentFuel;
    if (formData.liters > remaining) {
      advisories.push({
        id: 'fuel.overfill',
        severity: 'MEDIUM',
        title: 'Uplift exceeds remaining capacity',
        detail: `Remaining capacity is ~${Math.max(0, remaining)}L.`,
        suggestion: 'Reduce uplift quantity.',
      });
    }
  }

  return advisories;
};
